import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { useFirestore } from './FirestoreContext';
import { useAuth } from './AuthContext';
import { useMilestones } from './MilestoneDataContext';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';

const TimelineContext = createContext();

export const useTimeline = () => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error('useTimeline must be used within a TimelineProvider');
  }
  return context;
};

export const TimelineProvider = ({ children }) => {
  const { db } = useFirestore();
  const { currentUser } = useAuth();
  const [timelineData, setTimelineData] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const unsubscribeRefs = useRef({});

  const preloadTimelineData = async (incidentId) => {
    if (!incidentId || !currentUser?.uid) {
      console.log('Cannot preload timeline data: missing incidentId or currentUser');
      return;
    }

    // If already loading or loaded and data is fresh (less than 5 minutes old), don't reload
    const existingData = timelineData[incidentId];
    if (existingData && existingData.dataLoaded) {
      const dataAge = new Date() - existingData.loadedAt;
      if (dataAge < 5 * 60 * 1000) { // 5 minutes

        return;
      } else {
        console.log('Timeline data exists but is stale, reloading for incident:', incidentId);
        // Clear stale data
        clearTimelineData(incidentId);
      }
    }

    if (loadingStates[incidentId]) {
      // Timeline data already loading
      return;
    }

    // Preloading timeline data
    setLoadingStates(prev => ({ ...prev, [incidentId]: true }));

    try {
      // Get user's orgId first
      const userDocRef = doc(db, 'c3_users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.error('User document not found');
        setLoadingStates(prev => ({ ...prev, [incidentId]: false }));
        return;
      }

      const userOrgId = userDoc.data().orgId;


      let milestonesLoaded = false;
      let tasksLoaded = false;
      let milestones = [];
      let tasks = [];

      const checkDataLoaded = () => {
        if (milestonesLoaded && tasksLoaded) {
          setTimelineData(prev => ({
            ...prev,
            [incidentId]: {
              milestones,
              tasks,
              userOrgId,
              loadedAt: new Date(),
              dataLoaded: true
            }
          }));
          setLoadingStates(prev => ({ ...prev, [incidentId]: false }));
          // Timeline data preloaded successfully
        }
      };

      // 🎯 NOTE: Milestones now use shared data provider
      // No need for separate query - will be handled by MilestoneDataProvider

      // Fetch tasks
      const tasksRef = collection(db, 'c3_tasks');
      const tasksQuery = query(
        tasksRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', userOrgId),
        where('deleted', '==', false), // Exclude soft-deleted tasks
        orderBy('createdAt', 'desc')
      );

      // Set up real-time listeners
      // 🎯 Milestones handled by shared provider - mark as loaded
      milestones = []; // Will be populated by shared provider
      milestonesLoaded = true;
      checkDataLoaded();

      const unsubscribeTasks = onSnapshot(tasksQuery, 
        (snapshot) => {

          tasks = snapshot.docs.map(doc => {
            const taskData = doc.data();
            return {
              id: doc.id,
              type: 'task',
              ...taskData,
              dueDate: taskData.dueDate ? new Date(taskData.dueDate) : null
            };
          });
          tasksLoaded = true;
          checkDataLoaded();
        },
        (error) => {
          console.error('Error preloading tasks:', error);
          setLoadingStates(prev => ({ ...prev, [incidentId]: false }));
        }
      );

      // Store unsubscribe functions for cleanup
      unsubscribeRefs.current[incidentId] = {
        tasks: unsubscribeTasks
      };

    } catch (error) {
      console.error('Error setting up timeline preload:', error);
      setLoadingStates(prev => ({ ...prev, [incidentId]: false }));
    }
  };

  const getTimelineData = (incidentId) => {
    return timelineData[incidentId] || null;
  };

  const isTimelineLoading = (incidentId) => {
    return loadingStates[incidentId] || false;
  };

  const clearTimelineData = (incidentId) => {
    // Clean up listeners
    if (unsubscribeRefs.current[incidentId]) {
      // Only cleanup tasks - milestones now handled by MilestoneDataProvider
      if (unsubscribeRefs.current[incidentId].tasks) {
        unsubscribeRefs.current[incidentId].tasks();
      }
      delete unsubscribeRefs.current[incidentId];
    }

    // Clear data
    setTimelineData(prev => {
      const newData = { ...prev };
      delete newData[incidentId];
      return newData;
    });
    setLoadingStates(prev => {
      const newStates = { ...prev };
      delete newStates[incidentId];
      return newStates;
    });
  };

  const clearAllTimelineData = useCallback(() => {
    // Clean up all listeners
    Object.keys(unsubscribeRefs.current).forEach(incidentId => {
      if (unsubscribeRefs.current[incidentId]) {
        // Only cleanup tasks - milestones now handled by MilestoneDataProvider
        if (unsubscribeRefs.current[incidentId].tasks) {
          unsubscribeRefs.current[incidentId].tasks();
        }
      }
    });
    
    // Clear all data
    unsubscribeRefs.current = {};
    setTimelineData({});
    setLoadingStates({});
  }, []); // No dependencies needed - only accesses refs and setState

  const preloadMultipleIncidents = async (incidentIds) => {
    // Preloading timeline data for multiple incidents
    
    // Preload data for each incident
    const preloadPromises = incidentIds.map(incidentId => preloadTimelineData(incidentId));
    
    try {
      await Promise.all(preloadPromises);
      // All incident timeline data preloaded successfully
    } catch (error) {
      console.error('Error preloading multiple incidents:', error);
    }
  };

  const value = {
    preloadTimelineData,
    getTimelineData,
    isTimelineLoading,
    clearTimelineData,
    clearAllTimelineData,
    preloadMultipleIncidents
  };

  return (
    <TimelineContext.Provider value={value}>
      {children}
    </TimelineContext.Provider>
  );
}; 